/**
 * vitrinaConfig.js
 *
 * Two-tier, content-addressed loader for a salon's public config — talks only
 * to the vitrina's OWN edge backend (vitrina-gae-cdn /api), never the
 * salon-staff API (runapis).
 *
 * Tier 1 — manifest: GET {base}/api/config/{salon_id}
 *   → { salon_id, hashes: { config_type: hash } }   (short edge cache)
 * Tier 2 — blob:     GET {base}/api/config/{salon_id}/{type}/{hash}
 *   → { hash, data }                                (immutable, 1-year cache)
 *
 * Load strategy (see load_salon_configs):
 *   1. Hashes come from `window.__SALON__` when the edge injected them into
 *      index.html (production fast path — saves the manifest round-trip),
 *      otherwise from the manifest endpoint (localhost / fallback).
 *   2. Each hash is diffed against IndexedDB (utils/configDb.js); blobs whose
 *      hash already matches are reused locally — a returning visitor with no
 *      salon edits makes ZERO config API calls on the injected path.
 *   3. Only missing/changed blobs are fetched (in parallel) and persisted.
 *   4. A blob 404 means the hash went stale (salon edited within the HTML/
 *      manifest cache window) — the manifest is re-read once and that blob
 *      retried with the fresh hash.
 */

import { getVitrinaApiBase } from '@/js/utils/environment.js';
import { get_stored_configs, put_config } from '@/js/utils/configDb.js';

/**
 * Fetch the hash manifest for a salon.
 * @param {string|number} salon_id
 * @returns {Promise<{salon_id: string, hashes: Object<string, string>}>}
 */
export async function get_config_manifest(salon_id) {
  const url = `${getVitrinaApiBase()}/api/config/${salon_id}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`vitrina manifest ${res.status} for salon ${salon_id}`);
  }
  return res.json();
}

/**
 * Fetch one immutable config blob by its content hash.
 * @param {string|number} salon_id
 * @param {string} config_type
 * @param {string} hash
 * @returns {Promise<{hash: string, data: any}|null>} null when the hash is
 *   stale (404 — the salon edited since the manifest was read).
 */
export async function get_config_blob(salon_id, config_type, hash) {
  const url = `${getVitrinaApiBase()}/api/config/${salon_id}/${config_type}/${hash}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`vitrina blob ${res.status} for ${config_type}/${hash}`);
  }
  return res.json();
}

/**
 * Hashes injected by the edge into index.html, if present and for THIS salon.
 * @param {string|number} salon_id
 * @returns {{hashes: Object, public_info: (Object|undefined)}|null}
 */
function injected_bootstrap(salon_id) {
  const injected = typeof window !== 'undefined' ? window.__SALON__ : null;
  if (!injected || !injected.hashes) return null;
  if (String(injected.id) !== String(salon_id)) return null;
  return injected;
}

/**
 * Load the full public config for a salon: injected/manifest hashes, diffed
 * against IndexedDB, fetching only what changed.
 *
 * @param {string|number} salon_id
 * @returns {Promise<{salon_id: string,
 *   configs: Object<string, {hash: string, data: any}>,
 *   meta: {source: 'injected'|'manifest', reused: string[], fetched: string[],
 *          failed: string[]}}>}
 */
export async function load_salon_configs(salon_id) {
  const injected = injected_bootstrap(salon_id);
  let hashes = injected?.hashes || null;
  let source = injected ? 'injected' : 'manifest';
  if (!hashes) {
    hashes = (await get_config_manifest(salon_id)).hashes || {};
  }

  const stored = await get_stored_configs(salon_id);
  const configs = {};
  const reused = [];
  const fetched = [];
  const failed = [];
  const to_fetch = [];

  for (const [config_type, hash] of Object.entries(hashes)) {
    if (!hash) continue;
    // Edge-injected public_info carries its data inline — no fetch needed.
    if (config_type === 'public_info' && injected?.public_info) {
      configs[config_type] = { hash, data: injected.public_info };
      fetched.push(config_type);
      put_config(salon_id, config_type, hash, injected.public_info);
      continue;
    }
    if (stored[config_type]?.hash === hash) {
      configs[config_type] = stored[config_type];
      reused.push(config_type);
    } else {
      to_fetch.push([config_type, hash]);
    }
  }

  // One manifest re-read is allowed when a blob 404s (stale injected/cached
  // hash). Memoized so N stale blobs trigger a single refresh.
  let fresh_manifest_promise = null;
  const fresh_hash_for = async (config_type) => {
    if (!fresh_manifest_promise) {
      fresh_manifest_promise = get_config_manifest(salon_id);
    }
    const manifest = await fresh_manifest_promise;
    return (manifest.hashes || {})[config_type] || null;
  };

  await Promise.all(
    to_fetch.map(async ([config_type, hash]) => {
      try {
        let blob = await get_config_blob(salon_id, config_type, hash);
        if (!blob) {
          const fresh_hash = await fresh_hash_for(config_type);
          if (fresh_hash && fresh_hash !== hash) {
            blob = await get_config_blob(salon_id, config_type, fresh_hash);
          }
        }
        if (!blob) {
          failed.push(config_type);
          console.warn(`[vitrina] config '${config_type}' unavailable (stale hash)`);
          return;
        }
        configs[config_type] = { hash: blob.hash, data: blob.data };
        fetched.push(config_type);
        // Best-effort persistence — a failed write just means a re-fetch later.
        put_config(salon_id, config_type, blob.hash, blob.data);
      } catch (e) {
        failed.push(config_type);
        console.error(`[vitrina] config '${config_type}' load failed:`, e);
      }
    })
  );

  return { salon_id: String(salon_id), configs, meta: { source, reused, fetched, failed } };
}
